// Shared token-scan primitives for the EditorContext slot recognizers.
// PRD: GRA-8 (MSSQL projection pivot), design §2.2. G11: pure text scan,
// NO web-tree-sitter — these are surface-keyword/paren scans over a tokenised
// statement slice, the legal projection-side body the future tree-sitter swap
// replaces behind the identical signatures.
//
// GRA8-ROLL-1 extracted these from editorContextSlot.ts so the per-engine
// recognizer files (`editorContextSlot.<engine>.ts`), the window-frame
// detector, and the shared DDL-constraint detectors can all import them
// without a circular dependency on the dispatcher.

import type { ClauseToken } from './editorContextClause';
import { headTableVerb } from './editorContextSlot.ddl';
import { isInsideCreateTableColumnList } from './editorContextSlot.mssql';
import {
  isCollectionElementTypeSlot,
  isRoutineReturnTypeSlot,
} from './editorContextSlot.typeSlot';

/** Uppercased text of a keyword/identifier token, or `null` for other kinds. */
export function upper(t: ClauseToken | undefined): string | null {
  if (!t) return null;
  if (t.kind !== 'identifier' && t.kind !== 'keyword') return null;
  return t.text.toUpperCase();
}

/** True when an unclosed `(` encloses the cursor. */
export function hasUnclosedLparen(tokens: readonly ClauseToken[]): boolean {
  return findEnclosingLparenIndex(tokens) !== -1;
}

/**
 * Walk back from the cursor over a balanced stack of parens. Returns the
 * index of the unclosed `(` enclosing the cursor, or -1 when the cursor is
 * outside any unbalanced paren.
 */
export function findEnclosingLparenIndex(tokens: readonly ClauseToken[]): number {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.kind === 'rparen') depth += 1;
    else if (t.kind === 'lparen') {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
}

/**
 * True when the cursor sits inside an unclosed `(` whose immediately preceding
 * token is one of `callNames` (case-insensitive). Used to detect `CAST(...|`
 * shapes (the AS-arg inside).
 */
export function isInsideCallTo(
  tokens: readonly ClauseToken[],
  callNames: readonly string[],
): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  const before = upper(tokens[lparenIdx - 1]);
  if (!before) return false;
  return callNames.includes(before);
}

/**
 * True when the cursor is the EXISTING-COLUMN target of an `ALTER TABLE`
 * column operation: `DROP [COLUMN] ▮`, `ALTER [COLUMN] ▮`, `MODIFY [COLUMN] ▮`,
 * `CHANGE [COLUMN] ▮`, `RENAME COLUMN ▮`. Not inside any unclosed paren, and an
 * optional partial column identifier may already be typed.
 *
 * The DDL family (`classifyDdlSlot`) returns null for these — no paren, no
 * REFERENCES, no trigger — so without a recognizer the caret reached the
 * `unknown` arm, where the resolver emits tables and the altered table's own
 * columns never appeared at all. MySQL and SQLite each had a one-verb version
 * of this (`isMysqlDropColumnTargetSlot`, `isSqliteRenameColumnTargetSlot`);
 * this generalizes the shape so every engine's verbs are covered.
 *
 * Positions that name a NEW identifier — `ADD COLUMN ▮`, `RENAME TO ▮`,
 * `CHANGE old ▮` — deliberately do NOT match: an existing column is not what
 * belongs there.
 */
export function isAlterTableColumnTargetSlot(
  tokens: readonly ClauseToken[],
  offset: number,
): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  if (headTableVerb(tokens) !== 'alter') return false;

  let i = tokens.length - 1;
  const caretIsMidToken = tokens[i]?.end === offset;
  // Step over a column identifier — either one the user is still typing, or a
  // complete one. `COLUMN` lexes as an identifier, so only step over one that
  // is NOT the keyword itself.
  //
  // TYPED-PREFIX PARITY: a half-typed name the caret is ATTACHED to may lex as
  // a KEYWORD rather than an identifier — `DROP COLUMN not▮` (the user typing
  // `note`) tokenizes `not` as `NOT`, which is in SQL_KEYWORDS. Accepting the
  // keyword kind ONLY while the caret is attached keeps a ws-separated keyword
  // (real context) out: without it the verb scan looked at `NOT`, the slot
  // vanished and the altered table's columns disappeared on the first
  // keystroke (mysql coverage script.30 §8).
  const sawName =
    i >= 0 &&
    upper(tokens[i]) !== 'COLUMN' &&
    (tokens[i]!.kind === 'identifier' || (caretIsMidToken && tokens[i]!.kind === 'keyword'));
  if (sawName) i -= 1;
  // The optional `COLUMN` noise word.
  const hasColumnWord = upper(tokens[i]) === 'COLUMN';
  if (hasColumnWord) i -= 1;
  const verb = upper(tokens[i]);
  if (verb === null) return false;
  // `RENAME` without `COLUMN` is `RENAME TO <new name>`, a freeform position.
  if (verb === 'RENAME') return hasColumnWord;
  // PostgreSQL's `ALTER COLUMN <complete name> ▮` wants `TYPE` / `SET` / `DROP`,
  // not another column — and answering with columns hid the `TYPE` that leads
  // to the type slot. Only once a COMPLETE name is written: `ALTER COLUMN ▮`
  // and a half-typed name are both still the name position, and T-SQL's own
  // form is claimed earlier by `isMssqlAlterColumnTypeSlot`.
  if (verb === 'ALTER' && sawName && !caretIsMidToken) return false;
  return ALTER_COLUMN_VERBS.has(verb);
}

/** The verbs that take an existing column as their target. */
const ALTER_COLUMN_VERBS: ReadonlySet<string> = new Set(['DROP', 'ALTER', 'MODIFY', 'CHANGE']);

/**
 * Words that introduce a CONSTRAINT rather than name a column. None of them is
 * in `SQL_KEYWORDS`, so they lex as plain identifiers — and a recognizer that
 * accepts "any identifier" as the column name answers `ALTER TABLE t ADD
 * PRIMARY ▮` with the type list, hiding the `KEY` that belongs there.
 */
const CONSTRAINT_LEAD_WORDS: ReadonlySet<string> = new Set([
  'PRIMARY', 'UNIQUE', 'FOREIGN', 'CONSTRAINT', 'CHECK', 'EXCLUDE', 'FULLTEXT',
  'SPATIAL', 'INDEX', 'KEY', 'PERIOD',
]);

/** True when the token names a column rather than opening a constraint. */
function isColumnNameToken(token: ClauseToken | undefined): boolean {
  if (!token || token.kind !== 'identifier') return false;
  const u = token.text.toUpperCase();
  return u !== 'COLUMN' && !CONSTRAINT_LEAD_WORDS.has(u);
}

/**
 * True when the cursor is a DATA-TYPE position that every dialect shares:
 *
 *   `CAST(expr AS ▮)`                       — the cast target
 *   `CREATE TABLE t (col ▮`                 — a column definition's type
 *   `ALTER TABLE t ADD [COLUMN] col ▮`      — a new column's type
 *   `ALTER TABLE t MODIFY [COLUMN] col ▮`   — MySQL / Oracle
 *   `ALTER TABLE t CHANGE [COLUMN] a b ▮`   — MySQL
 *   `ALTER TABLE t ALTER COLUMN col TYPE ▮` — PostgreSQL
 *   `CREATE FUNCTION f(…) RETURNS ▮`        — PG / MySQL / T-SQL
 *   `CREATE FUNCTION f(…) RETURN ▮`         — Oracle PL/SQL
 *   `TYPE t IS TABLE OF ▮` / `VARRAY(n) OF ▮` — Oracle PL/SQL collections
 *
 * Only MSSQL had a recognizer for the first two, so MySQL floods at both
 * (`CREATE TABLE foo (id ▮` put `INT` at rank 97) and NO engine recognized the
 * ALTER forms — the type vocabulary was absent there entirely.
 *
 * `ALTER COLUMN col ▮` without `TYPE` is deliberately excluded: PG needs a
 * `TYPE` / `SET` / `DROP` keyword first, so a type is not what belongs there.
 *
 * TYPED-PREFIX PARITY: a caret ATTACHED to a trailing bare word (`RETURNS
 * tri▮`, `CREATE TABLE t (id IN▮`, `TABLE OF NUM▮`) is the same slot as the
 * bare caret — the attached word is the ANSWER the user is halfway through
 * typing, not context. Every shape below therefore runs against the token
 * stream with that partial DROPPED. Dropping it cannot turn a NAME position
 * into a type one: after the drop the tail is the `(` / `,` / `COLUMN` /
 * verb that made it a name position in the first place, and none of the
 * shapes matches those (`CREATE TABLE t (co▮` → tail `(`; `ADD COLUMN fo▮` →
 * tail `COLUMN`). The CAST arm keeps reading the RAW tail so an attached
 * `CAST(x AS▮` — which fires today — still does.
 */
export function isDataTypeTargetSlot(
  tokens: readonly ClauseToken[],
  /** Cursor offset. Distinguishes a caret ATTACHED to the last token (the
   *  partial answer, dropped below) from one separated by whitespace. */
  offset: number,
): boolean {
  const prev = tokens[tokens.length - 1];
  if (!prev) return false;

  // `CAST(expr AS ▮` — universal. (T-SQL's TRY_CAST / PARSE are covered by its
  // own recognizer, which runs first.)
  if (upper(prev) === 'AS' && hasUnclosedLparen(tokens) && isInsideCallTo(tokens, ['CAST'])) {
    return true;
  }

  const eff = dropAttachedPartial(tokens, offset);
  const tail = eff[eff.length - 1];
  if (!tail) return false;
  if (isColumnNameToken(tail) && isInsideCreateTableColumnList(eff)) return true;
  if (isRoutineReturnTypeSlot(eff)) return true;
  if (isCollectionElementTypeSlot(eff)) return true;
  return isAlterTableColumnTypeSlot(eff);
}

/**
 * Drop a trailing bare word (identifier / keyword) the caret is ATTACHED to —
 * the half-typed candidate, which is never part of the slot's own context.
 * Exported so the per-engine recognizers can share the same typed-prefix rule.
 */
export function dropAttachedPartial(
  tokens: readonly ClauseToken[],
  offset: number,
): readonly ClauseToken[] {
  const last = tokens[tokens.length - 1];
  if (!last || last.end !== offset) return tokens;
  if (last.kind !== 'identifier' && last.kind !== 'keyword') return tokens;
  return tokens.slice(0, -1);
}

/** The ALTER-TABLE shapes where a data type follows a COMPLETE column name. */
function isAlterTableColumnTypeSlot(tokens: readonly ClauseToken[]): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  if (headTableVerb(tokens) !== 'alter') return false;

  let i = tokens.length - 1;
  // PG's `ALTER COLUMN col TYPE ▮`.
  if (upper(tokens[i]) === 'TYPE') {
    i -= 1;
    if (!isColumnNameToken(tokens[i])) return false;
    i -= 1;
    if (upper(tokens[i]) === 'COLUMN') i -= 1;
    return upper(tokens[i]) === 'ALTER';
  }

  // `<verb> [COLUMN] <name> ▮`. The name must be a real one — a bare `COLUMN`
  // means the name itself is still being typed, and a constraint lead
  // (`ADD PRIMARY ▮`) names no column at all.
  if (!isColumnNameToken(tokens[i])) return false;
  i -= 1;
  let verb = upper(tokens[i]);
  if (verb === 'COLUMN') {
    i -= 1;
    verb = upper(tokens[i]);
  }
  if (verb === 'ADD' || verb === 'MODIFY') return true;
  // MySQL's `CHANGE [COLUMN] <old> <new> ▮` carries two names.
  if (tokens[i]?.kind === 'identifier') {
    i -= 1;
    let outer = upper(tokens[i]);
    if (outer === 'COLUMN') {
      i -= 1;
      outer = upper(tokens[i]);
    }
    return outer === 'CHANGE';
  }
  return false;
}

/**
 * T-SQL's `ALTER TABLE t ALTER COLUMN <name> ▮` — a data type follows the name
 * directly. PostgreSQL spells the same edit `ALTER COLUMN <name> TYPE <type>`,
 * so the shared recognizer above deliberately requires the `TYPE` keyword and
 * this engine-specific one fills in T-SQL's shorter form.
 */
export function isMssqlAlterColumnTypeSlot(
  tokens: readonly ClauseToken[],
  offset: number,
): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  if (headTableVerb(tokens) !== 'alter') return false;
  let i = tokens.length - 1;
  const name = tokens[i];
  // The name must be COMPLETE — a caret against it means it is still being typed.
  if (!isColumnNameToken(name)) return false;
  if (name!.end === offset) return false;
  i -= 1;
  if (upper(tokens[i]) !== 'COLUMN') return false;
  return upper(tokens[i - 1]) === 'ALTER';
}
